import type { MailFolder } from "owa-graph-schema";
import folderStore, { getEffectiveFolderDisplayName } from "owa-folders";
import { getStore as getFavoritesStore } from "owa-favorites";
import type { FavoriteFolderData } from "owa-favorites-types";
import publicFolderFavoriteStore from "owa-public-folder-favorite/lib/store/publicFolderFavoriteStore";
import loc, { format } from "owa-localize";
import { duplicateFavoriteDisplayNameTemplate, duplicateFavoriteParentFolderTemplate, duplicateFavoriteTooltipTemplate, } from "./FavoriteDisplayName.locstring.json";
import { getMailboxLabelForFavorite } from "./getMailboxLabelForFavorite";
import { owaComputedFn } from "owa-computed-fn";

function getFolder(folderId: string): MailFolder | undefined {
    if (publicFolderFavoriteStore().folderTable.has(folderId)) {
        return publicFolderFavoriteStore().folderTable.get(folderId);
    }
    
    return folderStore().folderTable.get(folderId);
}

// Builds a map of normalized display name → MailFolder[] from favorite folders.
// Recomputed by MobX only when the favorites or folder stores change.
const getFavoriteFoldersByDisplayName: () => Map<string, MailFolder[]> = owaComputedFn(function getFavoriteFoldersByDisplayName(): Map<string, MailFolder[]> {
    const map = new Map<string, MailFolder[]>();
    for (const fav of getFavoritesStore().outlookFavorites.values()) {
        if (fav.type === "folder" || fav.type?.startsWith("localFolder")) {
            const folder = getFolder((fav as FavoriteFolderData).folderId);
            if (folder) {
                const displayName = getEffectiveFolderDisplayName(folder);
                const key = displayName.toLocaleLowerCase();
                const existing = map.get(key);
                if (existing) {
                    existing.push(folder);
                }
                else {
                    map.set(key, [folder]);
                }
            }
        }
    }
    return map;
});

export default function getFavoriteFolderDisplayName(currentFolderId: string, useBothIdentifiers: boolean): string {
    const currentFolder = getFolder(currentFolderId);
    const currentFolderDisplayName = getEffectiveFolderDisplayName(currentFolder) ?? "";
    
    if (!currentFolder) {
        return currentFolderDisplayName;
    }
    
    const key = currentFolderDisplayName.toLocaleLowerCase();
    const foldersWithSameName = getFavoriteFoldersByDisplayName().get(key);
    const duplicateFolder = foldersWithSameName?.find(folder => folder.id !== currentFolderId);
    
    if (!duplicateFolder) {
        return currentFolderDisplayName;
    }
    
    const mailboxInfo = currentFolder.mailboxInfo;
    
    if (!mailboxInfo) {
        return currentFolderDisplayName;
    }
    const mailboxLabel = getMailboxLabelForFavorite(mailboxInfo);
    
    const isSameMailbox = mailboxInfo.mailboxSmtpAddress === duplicateFolder.mailboxInfo?.mailboxSmtpAddress;
    
    if (isSameMailbox) {
        const parentFolder = currentFolder.parentFolderId
            ? getFolder(currentFolder.parentFolderId)
            : undefined;
        const parentDisplayName = getEffectiveFolderDisplayName(parentFolder);
        if (parentDisplayName &&
            parentDisplayName !== "msgfolderroot" && // Avoid for top leve folders
            !!!currentFolder?.distinguishedFolderType // Avoid for distinguished folders since they have the same parent
        ) {
            return format(loc(duplicateFavoriteParentFolderTemplate), currentFolderDisplayName, parentDisplayName);
        }
        return currentFolderDisplayName;
    }
    
    if (useBothIdentifiers && mailboxLabel && mailboxLabel !== mailboxInfo.mailboxSmtpAddress) {
        return format(loc(duplicateFavoriteTooltipTemplate), currentFolderDisplayName, mailboxLabel, mailboxInfo.mailboxSmtpAddress);
    }
    
    return format(loc(duplicateFavoriteDisplayNameTemplate), currentFolderDisplayName, mailboxLabel ?? mailboxInfo.mailboxSmtpAddress);
}
